import { createSignal } from "solid-js";
import type { NavigateFn, useLocation } from "@tanstack/solid-router";

import { closestSelector, isEditableTarget, isEditingActive } from "@/lib/gestures/guards";
import { createGestureRecognizer } from "@/lib/gestures/recognizer";
import { anyFlag, pointerHandlers } from "@/lib/gestures/solid";
import { PAGE_SWIPE } from "@/lib/gestures/presets";
import { useInteraction } from "@fleet/ui";

interface PageSwipeGesturesOptions {
  location: ReturnType<typeof useLocation>;
  navigate: NavigateFn;
  /** Ordered tab routes the horizontal swipe pages through. */
  orderedPaths: () => string[];
}

const {
  thresholdPx: THRESHOLD_PX,
  indicatorTriggerPx: SWIPE_INDICATOR_TRIGGER_PX,
  commitThresholdPx: SWIPE_COMMIT_THRESHOLD_PX,
  rubberBandFactor: RUBBER_BAND_FACTOR,
  transitionMs: SWIPE_TRANSITION_MS,
  fallbackWidthPx: SWIPE_FALLBACK_WIDTH_PX,
  fling: PAGE_FLING,
} = PAGE_SWIPE;

// Flags that suppress page swiping entirely (an overlay owns the gesture).
// `nav-drawer.open` matters even though the drawer excludes `aside` targets
// below: this listener is bound to the WINDOW, so a drag starting on the
// drawer's backdrop would otherwise page-swipe underneath the open drawer.
const SUPPRESS_FLAGS = ["dialog.open", "drawer.open", "nav-drawer.open", "palette.open"] as const;

/**
 * Horizontal swipe to page between the top-level tabs (Today / Calendar / Stats),
 * mirroring the canon list-swipe in listto-app but trimmed to a single axis: Habits
 * has no pull-to-dashboard (Today *is* the landing screen).
 *
 * The shared recognizer owns the physics (deadzone, axis-lock, fling). This hook
 * only maps a committed horizontal swipe to the previous/next tab and exposes a
 * rubber-banded offset for the page-to-page slide.
 *
 * Surfaces that own their own horizontal carousel (the calendar's month pager)
 * mark themselves with `data-swipe-own="true"`; a gesture starting inside one is
 * ignored here so the inner carousel keeps the axis. Drags on the sidebar, on
 * inputs, or while a dialog/drawer/palette is open are likewise ignored.
 */
export function usePageSwipeGestures(options: PageSwipeGesturesOptions) {
  const interaction = useInteraction();

  const [swipeTx, setSwipeTx] = createSignal(0);
  const [isSwiping, setIsSwiping] = createSignal(false);
  const [swipeAnimating, setSwipeAnimating] = createSignal(false);

  // Captured at gesture start (canStart), read at engage time.
  let isOwnSwipeTarget = false;
  let isInputTarget = false;

  const currentIndex = () => options.orderedPaths().indexOf(options.location().pathname);

  /** Page to the previous/next tab, clamping at the ends. */
  const navigateTab = (isNext: boolean) => {
    const paths = options.orderedPaths();
    const index = currentIndex();
    if (index < 0) {
      return;
    }
    const nextIndex = isNext ? index + 1 : index - 1;
    const target = paths[nextIndex];
    if (target) {
      void options.navigate({ to: target });
    }
  };

  const recognizer = createGestureRecognizer({
    axis: "x",
    thresholdPx: THRESHOLD_PX,
    fling: PAGE_FLING,
    canStart: ({ target }) => {
      // An open dialog / drawer / palette owns input — never page-swipe.
      if (anyFlag(interaction, SUPPRESS_FLAGS)) {
        return false;
      }
      // Only on one of the ordered tab routes.
      if (!options.orderedPaths().includes(options.location().pathname)) {
        return false;
      }
      // Gestures starting inside a sidebar are ignored.
      if (closestSelector(target, "aside")) {
        return false;
      }
      // Yield to a surface that owns its own horizontal carousel.
      isOwnSwipeTarget = closestSelector(target, '[data-swipe-own="true"]');
      isInputTarget = isEditableTarget(target);
      return true;
    },
    canEngage: (_axis, _sample) =>
      // Horizontal swipe yields to inner carousels, inputs and text selection.
      !(isOwnSwipeTarget || isInputTarget || isEditingActive()),
    onStart: () => {
      setIsSwiping(true);
      setSwipeAnimating(false);
    },
    onMove: (sample) => {
      // Prevent text selection during the drag.
      globalThis.getSelection()?.removeAllRanges();
      const index = currentIndex();
      const paths = options.orderedPaths();
      // Rubber-band when there is no tab to slide to in the swipe direction.
      const atStart = index <= 0 && sample.dx > 0;
      const atEnd = index >= paths.length - 1 && sample.dx < 0;
      const effectiveDx = atStart || atEnd ? sample.dx * RUBBER_BAND_FACTOR : sample.dx;
      setSwipeTx(effectiveDx);
    },
    onEnd: (sample) => {
      const { dx } = sample;
      const index = currentIndex();
      const paths = options.orderedPaths();
      const isNext = dx < 0;
      const hasTarget = isNext ? index < paths.length - 1 : index > 0;
      const shouldCommit =
        hasTarget && index >= 0 && (sample.isFling || Math.abs(dx) > SWIPE_COMMIT_THRESHOLD_PX);
      setSwipeAnimating(true);
      if (shouldCommit) {
        const width = globalThis.innerWidth || SWIPE_FALLBACK_WIDTH_PX;
        setSwipeTx(isNext ? -width : width);
        setTimeout(() => {
          navigateTab(isNext);
          setSwipeTx(0);
          setIsSwiping(false);
          setSwipeAnimating(false);
        }, SWIPE_TRANSITION_MS);
      } else {
        setSwipeTx(0);
        setTimeout(() => {
          setIsSwiping(false);
          setSwipeAnimating(false);
        }, SWIPE_TRANSITION_MS);
      }
    },
    onCancel: () => {
      setIsSwiping(false);
      setSwipeAnimating(true);
      setSwipeTx(0);
      setTimeout(() => {
        setSwipeAnimating(false);
      }, SWIPE_TRANSITION_MS);
    },
  });

  return {
    handlers: pointerHandlers(recognizer),
    swipeTx,
    isSwiping,
    swipeAnimating,
    SWIPE_INDICATOR_TRIGGER_PX,
    SWIPE_COMMIT_THRESHOLD_PX,
  };
}
